import { CaretSortIcon, ChevronDownIcon } from "@radix-ui/react-icons";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import ChatMultipleSearchResults from "@/components/ChatMultipleSearchResults/chatMultipleSearchResults.component";
import CreateTaskTags from "@/components/CreateTaskTags/createTaskTags.component";
import { DeleteTaskDialog } from "@/components/Dialogs/deleteTaskDialog.component";
import DueDatePicker from "@/components/DueDatePicker/dueDatePicker.component";
import FavoriteSearchDialog from "@/components/FavoriteSearchDialog/favoriteSearchDialog.component";
import AssigneePicker from "@/components/PersonPickerDropdown/assigneePicker.component";
import CollaboratorPicker from "@/components/CollaboratorPicker/collaboratorPicker.component";
import SingleTaskViewSheet from "@/components/SingleTaskViewSheet/singleTaskViewSheet.component";
import ViewSearchResultsRow from "@/components/ViewSearchResults/viewSearchResultsRow.component";
import ViewSearchResultsRowMobile from "@/components/ViewSearchResults/viewSearchResultsRowMobile.component";
import { ROUTE_SEARCH } from "@/constants/routes.constants";
import { useMediaQuery } from "@/hooks/AppContext/mediaQuery";
import {
  advancedSearchTasks,
  sendDeleteTasks,
  updateTask,
  updateTaskStatus,
} from "@/models/tasks.model";
import { AppContext } from "@/services/AppContext/appContext";
import { Button } from "@/shadcn/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shadcn/components/ui/dialog";
import { Input } from "@/shadcn/components/ui/input";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/shadcn/components/ui/resizable";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shadcn/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/components/ui/tooltip";
import {
  AllURLSearchFilterValues,
  Collaborator,
  EditedTask,
  NewTaskTag,
  SearchTaskAdvanced,
  SearchTaskBasic,
  Tag,
} from "@/types";
import {
  filterSearchAssignedBy,
  filterSearchAssignedTo,
  filterSearchCollaborators,
  filterSearchCompletedDate,
  filterSearchCreatedBy,
  filterSearchCreatedDate,
  filterSearchDueDate,
  filterSearchHasUsersTimer,
  filterSearchModifiedDate,
  filterSearchStatus,
  filterSearchTags,
  filterSearchTitle,
  filterSearchUpdatedBy,
} from "@/utils/taskSearch.util";
import {
  formatDueDate,
  genericFailureToast,
  genericSuccessToast,
  getNewTaskTagByID,
} from "@/utils/utils";
import { Checkbox } from "@/shadcn/components/ui/checkbox";
import { ChatBubbleIcon } from "@radix-ui/react-icons";
import { motion } from "framer-motion";
import {
  Calendar,
  CheckCircle,
  Circle,
  Square,
  Tags,
  Trash,
  User,
  Users,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router";
import Swal from "sweetalert2";
type SearchTaskUnion = SearchTaskBasic | SearchTaskAdvanced;

// type ViewSearchResultsProps = {};

type ViewSearchResultsProps = {
  URLFilters: AllURLSearchFilterValues;
};

const ViewSearchResults: React.FC<ViewSearchResultsProps> = ({
  URLFilters,
}) => {
  const {
    precacheTasks,
    getPrecacheTasks,
    allAppUsers,
    forceFetchAll,
    userTags,
  } = useContext(AppContext);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isBulkTagsDialogOpen, setIsBulkTagsDialogOpen] = useState(false);
  const [isBulkCollaboratorsDialogOpen, setIsBulkCollaboratorsDialogOpen] =
    useState(false);
  const [isBulkAssigneeDialogOpen, setIsBulkAssigneeDialogOpen] =
    useState(false);
  const [isBulkDueDateDialogOpen, setIsBulkDueDateDialogOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [data, setData] = useState<SearchTaskBasic[]>([]);
  const [advancedTasks, setAdvancedTasks] = useState<SearchTaskAdvanced[]>([]);
  const [isAdvancedMode, setIsAdvancedMode] = useState<boolean>(false);
  const [displayData, setDisplayData] = useState<SearchTaskUnion[]>([]);
  const [isLoadingAdvanced, setIsLoadingAdvanced] = useState<boolean>(false);
  const [advancedError, setAdvancedError] = useState<string | null>(null);
  const location = useLocation();
  // const params = new URLSearchParams();
  const nav = useNavigate();
  const isNotSmall = useMediaQuery("sm");
  const [bulkActionSubmitting, setBulkActionSubmitting] = useState(false);
  const [bulkTags, setBulkTags] = useState<NewTaskTag[]>([]);
  const [bulkTagAction, setBulkTagAction] = useState<"add" | "remove">("add");
  const [bulkCollaborators, setBulkCollaborators] = useState<number[]>([]);
  const [bulkCollaboratorAction, setBulkCollaboratorAction] = useState<
    "add" | "remove"
  >("add");
  const [bulkAssignee, setBulkAssignee] = useState<number | null>(null);
  const [bulkDueDateAction, setBulkDueDateAction] = useState<"set" | "clear">(
    "set",
  );
  const [bulkDueDate, setBulkDueDate] = useState<Date | null | undefined>(
    undefined,
  );
  const [bulkEditVersion, setBulkEditVersion] = useState(0);
  const [
    bulkCollaboratorsDialogContainer,
    setBulkCollaboratorsDialogContainer,
  ] = useState<HTMLDivElement | null>(null);

  // Is filtering
  const [hasFiltering, setHasFiltering] = useState<boolean>(false);

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const originalDataRef = useRef<SearchTaskBasic[]>([]);
  const bulkCollaboratorRef = useRef<HTMLDivElement | null>(null);
  const { taskID } = useParams();

  const [viewingTaskID, setViewingTaskID] = useState<number | null>(null);

  const [chatWithSearchResults, setChatWithSearchResults] =
    useState<boolean>(false);

  useEffect(() => {
    if (!taskID) {
      setViewingTaskID(null);
      return;
    }
    // check if number
    const taskIDNum = Number(taskID);
    if (isNaN(taskIDNum)) {
      console.error("Task ID is not a number");
      Swal.fire({
        title: "Error",
        text: "Task ID is not a number",
        icon: "error",
      });
      setViewingTaskID(null);
      return;
    }
    setViewingTaskID(taskIDNum);
  }, [taskID]);

  // Check if advanced filters are being used
  const isUsingAdvancedFilters = useMemo(() => {
    return Boolean(
      URLFilters.createdBy ||
        URLFilters.updatedBy ||
        URLFilters.assignedBy ||
        URLFilters.hasUsersTimer ||
        URLFilters.createdDateData ||
        URLFilters.createdDateType ||
        URLFilters.modifiedDateData ||
        URLFilters.modifiedDateType ||
        URLFilters.completedDateData ||
        URLFilters.completedDateType,
    );
  }, [URLFilters]);

  const bulkCollaboratorSelections = useMemo<Collaborator[]>(
    () =>
      allAppUsers
        .filter((user) => bulkCollaborators.includes(user.id))
        .map((user) => ({
          id: user.id,
          full_name: `${user.first_name} ${user.surname}`.trim(),
          email: user.email,
        })),
    [allAppUsers, bulkCollaborators],
  );

  // Apply Filter data
  const applyFilters = useCallback(
    (incomingData: SearchTaskBasic[]): SearchTaskBasic[] => {
      let allData: SearchTaskBasic[] = [...incomingData];

      // If we're using advanced filters but don't have advanced data, return empty array
      if (isUsingAdvancedFilters && !isAdvancedMode) {
        return [];
      }

      // filterTitle()
      allData = allData.filter((task) => {
        try {
          // Basic filters that work with both basic and advanced data
          const includesTitle = filterSearchTitle(URLFilters.title, task);
          const includesStatus = filterSearchStatus(URLFilters.status, task);
          const includesAssignedTo = filterSearchAssignedTo(
            URLFilters.assignedTo,
            task,
          );
          const includesCollaborators = filterSearchCollaborators(
            URLFilters.collaborators,
            URLFilters.collaboratorSearchType,
            task,
          );
          const includesTags = filterSearchTags(
            URLFilters.tagData,
            URLFilters.tagType,
            task,
          );
          const includesDueDate = filterSearchDueDate(
            URLFilters.dueDateData,
            URLFilters.dueDateType,
            task,
          );

          // Advanced filters - only apply if we have advanced data
          let includesCreatedBy = true;
          let includesUpdatedBy = true;
          let includesAssignedBy = true;
          let includesHasUsersTimer = true;
          let includesCreatedDate = true;
          let includesModifiedDate = true;
          let includesCompletedDate = true;

          if (isAdvancedMode) {
            const advancedTask = task as SearchTaskAdvanced;
            includesCreatedBy = filterSearchCreatedBy(
              URLFilters.createdBy,
              advancedTask,
              allAppUsers,
            );
            includesUpdatedBy = filterSearchUpdatedBy(
              URLFilters.updatedBy,
              advancedTask,
              allAppUsers,
            );
            includesAssignedBy = filterSearchAssignedBy(
              URLFilters.assignedBy,
              advancedTask,
              allAppUsers,
            );
            includesHasUsersTimer = filterSearchHasUsersTimer(
              URLFilters.hasUsersTimer,
              advancedTask,
              allAppUsers,
            );
            includesCreatedDate = filterSearchCreatedDate(
              URLFilters.createdDateData,
              URLFilters.createdDateType,
              advancedTask,
            );
            includesModifiedDate = filterSearchModifiedDate(
              URLFilters.modifiedDateData,
              URLFilters.modifiedDateType,
              advancedTask,
            );
            includesCompletedDate = filterSearchCompletedDate(
              URLFilters.completedDateData,
              URLFilters.completedDateType,
              advancedTask,
            );
          }

          const isInverseTitle =
            URLFilters.inverseFilters?.includes("title") || false;
          const isInverseAssignedTo =
            URLFilters.inverseFilters?.includes("assignedTo") || false;
          const isInverseCollaborators =
            URLFilters.inverseFilters?.includes("collaborators") || false;
          const isInverseTags =
            URLFilters.inverseFilters?.includes("tag") || false;
          const isInverseCreatedBy =
            URLFilters.inverseFilters?.includes("createdBy") || false;
          const isInverseUpdatedBy =
            URLFilters.inverseFilters?.includes("updatedBy") || false;
          const isInverseAssignedBy =
            URLFilters.inverseFilters?.includes("assignedBy") || false;
          const isInverseHasUsersTimer =
            URLFilters.inverseFilters?.includes("hasUsersTimer") || false;
          const isInverseDueDate =
            URLFilters.inverseFilters?.includes("dueDate") || false;
          const isInverseCreatedDate =
            URLFilters.inverseFilters?.includes("createdDate") || false;
          const isInverseModifiedDate =
            URLFilters.inverseFilters?.includes("modifiedDate") || false;
          const isInverseCompletedDate =
            URLFilters.inverseFilters?.includes("completedDate") || false;

          const included =
            (isInverseTitle ? !includesTitle : includesTitle) &&
            includesStatus &&
            (isInverseAssignedTo ? !includesAssignedTo : includesAssignedTo) &&
            (isInverseCollaborators
              ? !includesCollaborators
              : includesCollaborators) &&
            (isInverseTags ? !includesTags : includesTags) &&
            (isInverseCreatedBy ? !includesCreatedBy : includesCreatedBy) &&
            (isInverseUpdatedBy ? !includesUpdatedBy : includesUpdatedBy) &&
            (isInverseAssignedBy ? !includesAssignedBy : includesAssignedBy) &&
            (isInverseHasUsersTimer
              ? !includesHasUsersTimer
              : includesHasUsersTimer) &&
            (isInverseDueDate ? !includesDueDate : includesDueDate) &&
            (isInverseCreatedDate
              ? !includesCreatedDate
              : includesCreatedDate) &&
            (isInverseModifiedDate
              ? !includesModifiedDate
              : includesModifiedDate) &&
            (isInverseCompletedDate
              ? !includesCompletedDate
              : includesCompletedDate);

          return included;
        } catch (error) {
          console.error("Error filtering data", error);
          return false; // Exclude the task if there's an error
        }
      });

      return allData;
    },
    [URLFilters, allAppUsers, isAdvancedMode, isUsingAdvancedFilters],
  );

  // Handle precacheTasks changes
  useLayoutEffect(() => {
    if (precacheTasks) {
      // Update original data reference
      originalDataRef.current = precacheTasks;

      // Apply existing filters
      const filtered = applyFilters(originalDataRef.current);
      setData(filtered);
    }
  }, [precacheTasks, applyFilters]);

  const fetchAdvancedData = async () => {
    setIsLoadingAdvanced(true);
    setAdvancedError(null);
    const response = await advancedSearchTasks({ all: true });

    if (response.rStatus === "error") {
      setAdvancedError(
        response.message ||
          "Failed to load advanced search data. Please try again.",
      );
      setIsLoadingAdvanced(false);
      // Stay in basic mode if advanced data fetch fails
      setIsAdvancedMode(false);
      setAdvancedTasks([]);
      return;
    }

    setAdvancedTasks(response.tasks);
    setIsAdvancedMode(true);
    setIsLoadingAdvanced(false);
    return;
  };

  // Effect to handle advanced filter detection and data fetching
  useEffect(() => {
    if (isUsingAdvancedFilters && !isAdvancedMode) {
      // Switch to advanced mode and fetch advanced data

      fetchAdvancedData();
    } else if (!isUsingAdvancedFilters && isAdvancedMode) {
      // Switch back to basic mode
      setIsAdvancedMode(false);
      setAdvancedTasks([]);
      setAdvancedError(null);
    }
  }, [isUsingAdvancedFilters, isAdvancedMode]);

  // Update display data based on current mode and data
  useEffect(() => {
    console.log(isAdvancedMode);
    if (isAdvancedMode) {
      if (advancedTasks.length > 0) {
        // Use advanced data
        const filteredAdvanced = applyFilters(
          advancedTasks as SearchTaskBasic[],
        );
        setDisplayData(filteredAdvanced);
      } else {
        // If advanced mode is on but we don't have advanced data yet, show empty
        setDisplayData([]);
      }
    } else if (isUsingAdvancedFilters && isLoadingAdvanced) {
      // If we're using advanced filters and currently loading, show empty
      setDisplayData([]);
    } else {
      // Use basic data
      setDisplayData(data);
    }
  }, [
    isAdvancedMode,
    advancedTasks,
    data,
    applyFilters,
    isUsingAdvancedFilters,
    isLoadingAdvanced,
  ]);

  // Clear filters
  function clearFilters() {
    console.log("Clearing filters");
    console.log(precacheTasks);
    setData(precacheTasks ?? []);

    table.setColumnFilters([]);
    table.setSorting([]);
    table.setRowSelection({});
    nav(ROUTE_SEARCH);
  }

  const columns: ColumnDef<SearchTaskUnion>[] = [
    {
      accessorKey: "select",
      id: "select",
      enableSorting: false,
      enableHiding: false,
      header: ({ table }) => (
        <div className="flex items-center h-full">
          <Checkbox
            id="select-all"
            checked={
              table.getIsAllRowsSelected() ||
              (table.getIsSomeRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) => {
              table.toggleAllRowsSelected(!!value);
            }}
            aria-label="Select all rows"
          />
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: "",
    },
    {
      accessorKey: "title",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="px-0"
        >
          Title
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
    },
    {
      accessorKey: "due_date",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="px-0"
        >
          Due Date
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
    },
    {
      accessorKey: "assignee_name",
      header: () => <div>Assignee</div>,
    },
    {
      accessorKey: "collaborators",
      header: "Collaborators",
    },
    {
      accessorKey: "tags",
      header: () => (
        <div className="grid grid-cols-2 items-center">
          <div>Projects</div>
          <div>Tags</div>
        </div>
      ),
    },
    {
      id: "actions",
      enableHiding: false,
    },
  ];

  const table = useReactTable({
    data: displayData,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    autoResetPageIndex: false,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  function getFilteredTaskIDs() {
    const selectedRows = table
      .getFilteredRowModel()
      .rows.map((row) => row.original.id);
    return selectedRows;
  }

  useEffect(() => {
    if (!table || !columnVisibility) return;

    // if !isNotSmall, change the selected columns to status, title and due_date
    if (!isNotSmall) {
      const newColumnVisibility = {
        select: true,
        status: true,
        title: true,
        due_date: true,
        assignee_name: true,
        collaborators: false,
        tags: false,
      };
      table.setColumnVisibility(newColumnVisibility);
    } else {
      // Reset to original column visibility
      table.setColumnVisibility({ ...columnVisibility });
    }
  }, [isNotSmall]);

  function handleTitleSearch(value: string) {
    const searchParams = new URLSearchParams(location.search);
    // edit the title param
    if (value) {
      searchParams.set("title", value);
    } else {
      searchParams.delete("title");
    }
    // set the search params
    nav(`${ROUTE_SEARCH}?${searchParams.toString()}`, {
      replace: true,
    });
  }

  async function handleBulkDeleteTasks() {
    const selectedRows = table
      .getSelectedRowModel()
      .rows.map((row) => row.original.id);
    console.log("Selected ids", selectedRows);

    const res = await sendDeleteTasks({ taskIDs: selectedRows });
    setIsDeleteDialogOpen(false);
    if (res.rStatus == "error") {
      console.error("Error deleting tasks", res.message);
      genericFailureToast("Error deleting tasks");
      return;
    }

    genericSuccessToast("Tasks deleted successfully");
    table.setPageIndex(0);
    table.setRowSelection({});
    await forceFetchAll();
    setBulkEditVersion((prev) => prev + 1);
  }

  const buildEditedTask = useCallback(
    (taskID: number, updates: Partial<EditedTask>): EditedTask => ({
      id: taskID,
      status: undefined,
      title: undefined,
      due_date: undefined,
      input: undefined,
      collaborators: undefined,
      tags: undefined,
      assigned_to: undefined,
      parent_id: undefined,
      ...updates,
    }),
    [],
  );

  const findTagByName = useCallback(
    (tagName: string, color?: string): NewTaskTag | undefined => {
      const availableTags = userTags ?? [];
      for (const category of availableTags) {
        const matched = category.tags.find((tag) => {
          if (color) {
            return tag.name === tagName && tag.color === color;
          }
          return tag.name === tagName;
        });
        if (matched) {
          return {
            category_id: category.id,
            tag: matched.name,
            color: matched.color,
          };
        }
      }
      return undefined;
    },
    [userTags],
  );

  const normalizeTaskTags = useCallback(
    (taskTags: Tag[]): NewTaskTag[] => {
      const availableTags = userTags ?? [];
      return taskTags
        .map((tag) => {
          const tagRecord = tag as Tag & {
            tag?: string;
            category_id?: number;
          };
          const tagName = tagRecord.tag ?? tag.name;
          const tagColor = tag.color;
          if (
            typeof tagRecord.category_id === "number" &&
            typeof tagRecord.tag === "string"
          ) {
            return {
              category_id: tagRecord.category_id,
              tag: tagRecord.tag,
              color: tagColor,
            } as NewTaskTag;
          }
          if (availableTags.length) {
            const byId = getNewTaskTagByID(tag.id, availableTags);
            if (byId) {
              return byId;
            }
            if (tagName) {
              return findTagByName(tagName, tagColor);
            }
          }
          return undefined;
        })
        .filter((tag): tag is NewTaskTag => Boolean(tag));
    },
    [findTagByName, userTags],
  );

  const tagKey = useCallback(
    (tag: NewTaskTag) => `${tag.category_id}:${tag.tag}`,
    [],
  );

  const mergeTags = useCallback(
    (existing: NewTaskTag[], incoming: NewTaskTag[]) => {
      const map = new Map(existing.map((tag) => [tagKey(tag), tag]));
      incoming.forEach((tag) => map.set(tagKey(tag), tag));
      return Array.from(map.values());
    },
    [tagKey],
  );

  const removeTags = useCallback(
    (existing: NewTaskTag[], remove: NewTaskTag[]) => {
      const removeKeys = new Set(remove.map((tag) => tagKey(tag)));
      return existing.filter((tag) => !removeKeys.has(tagKey(tag)));
    },
    [tagKey],
  );

  const mergeCollaborators = useCallback(
    (existing: number[], incoming: number[], action: "add" | "remove") => {
      const set = new Set(existing);
      incoming.forEach((id) => {
        if (action === "add") {
          set.add(id);
        } else {
          set.delete(id);
        }
      });
      return Array.from(set);
    },
    [],
  );

  const runBulkTaskUpdate = useCallback(
    async (
      createTaskUpdate: (task: SearchTaskUnion) => EditedTask,
      successMessage: string,
    ) => {
      if (bulkActionSubmitting) return;
      const selectedTasks = table
        .getSelectedRowModel()
        .rows.map((row) => row.original);

      if (!selectedTasks.length) {
        genericFailureToast("No tasks selected");
        return;
      }

      setBulkActionSubmitting(true);
      try {
        const results = await Promise.all(
          selectedTasks.map((task) =>
            updateTask({ task: createTaskUpdate(task) }),
          ),
        );
        const failedCount = results.filter(
          (result) => result.rStatus === "error",
        ).length;
        if (failedCount > 0) {
          genericFailureToast(
            `Failed to update ${failedCount} of ${selectedTasks.length} tasks`,
          );
        } else {
          genericSuccessToast(successMessage);
        }
        table.setRowSelection({});
        await forceFetchAll();
        setBulkEditVersion((prev) => prev + 1);
      } finally {
        setBulkActionSubmitting(false);
      }
    },
    [bulkActionSubmitting, forceFetchAll, table],
  );

  const runBulkStatusUpdate = useCallback(
    async (status: "complete" | "incomplete") => {
      if (bulkActionSubmitting) return;
      const selectedTasks = table
        .getSelectedRowModel()
        .rows.map((row) => row.original);

      if (!selectedTasks.length) {
        genericFailureToast("No tasks selected");
        return;
      }

      setBulkActionSubmitting(true);
      try {
        const results = await Promise.all(
          selectedTasks.map((task) =>
            updateTaskStatus({ taskID: task.id, status }),
          ),
        );
        const failedCount = results.filter(
          (result) => result.rStatus === "error",
        ).length;
        if (failedCount > 0) {
          genericFailureToast(
            `Failed to update ${failedCount} of ${selectedTasks.length} tasks`,
          );
        } else {
          genericSuccessToast(
            status === "complete"
              ? "Tasks marked complete"
              : "Tasks marked incomplete",
          );
        }
        table.setRowSelection({});
        await forceFetchAll();
        setBulkEditVersion((prev) => prev + 1);
      } finally {
        setBulkActionSubmitting(false);
      }
    },
    [bulkActionSubmitting, forceFetchAll, table],
  );

  const closeBulkTagsDialog = useCallback(() => {
    setIsBulkTagsDialogOpen(false);
    setBulkTags([]);
    setBulkTagAction("add");
  }, []);

  const closeBulkCollaboratorsDialog = useCallback(() => {
    setIsBulkCollaboratorsDialogOpen(false);
    setBulkCollaborators([]);
    setBulkCollaboratorAction("add");
  }, []);

  const closeBulkAssigneeDialog = useCallback(() => {
    setIsBulkAssigneeDialogOpen(false);
    setBulkAssignee(null);
  }, []);

  const closeBulkDueDateDialog = useCallback(() => {
    setIsBulkDueDateDialogOpen(false);
    setBulkDueDateAction("set");
    setBulkDueDate(undefined);
  }, []);

  const handleBulkTagsApply = useCallback(async () => {
    if (!bulkTags.length) {
      genericFailureToast("Select at least one tag");
      return;
    }
    if (!userTags || userTags.length === 0) {
      genericFailureToast("Tags are still loading");
      return;
    }
    await runBulkTaskUpdate(
      (task) => {
        const existingTags = normalizeTaskTags(task.tags ?? []);
        const nextTags =
          bulkTagAction === "add"
            ? mergeTags(existingTags, bulkTags)
            : removeTags(existingTags, bulkTags);
        return buildEditedTask(task.id, { tags: nextTags });
      },
      bulkTagAction === "add"
        ? "Tags added to tasks"
        : "Tags removed from tasks",
    );
    closeBulkTagsDialog();
  }, [
    bulkTags,
    userTags,
    runBulkTaskUpdate,
    normalizeTaskTags,
    bulkTagAction,
    mergeTags,
    removeTags,
    buildEditedTask,
    closeBulkTagsDialog,
  ]);

  const handleBulkCollaboratorsApply = useCallback(async () => {
    if (!bulkCollaborators.length) {
      genericFailureToast("Select at least one collaborator");
      return;
    }
    await runBulkTaskUpdate(
      (task) => {
        const nextCollaborators = mergeCollaborators(
          task.collaborators ?? [],
          bulkCollaborators,
          bulkCollaboratorAction,
        );
        return buildEditedTask(task.id, {
          collaborators: nextCollaborators,
        });
      },
      bulkCollaboratorAction === "add"
        ? "Collaborators added to tasks"
        : "Collaborators removed from tasks",
    );
    closeBulkCollaboratorsDialog();
  }, [
    bulkCollaborators,
    runBulkTaskUpdate,
    mergeCollaborators,
    bulkCollaboratorAction,
    buildEditedTask,
    closeBulkCollaboratorsDialog,
  ]);

  const handleBulkAssigneeApply = useCallback(async () => {
    if (!bulkAssignee) {
      genericFailureToast("Select an assignee");
      return;
    }
    await runBulkTaskUpdate(
      (task) => buildEditedTask(task.id, { assigned_to: bulkAssignee }),
      "Tasks assigned",
    );
    closeBulkAssigneeDialog();
  }, [
    bulkAssignee,
    runBulkTaskUpdate,
    buildEditedTask,
    closeBulkAssigneeDialog,
  ]);

  const handleBulkDueDateApply = useCallback(async () => {
    if (bulkDueDateAction === "set") {
      if (!bulkDueDate) {
        genericFailureToast("Pick a due date");
        return;
      }
      const formatted = formatDueDate(bulkDueDate);
      if (!formatted) {
        genericFailureToast("Invalid due date");
        return;
      }
      await runBulkTaskUpdate(
        (task) => buildEditedTask(task.id, { due_date: formatted }),
        "Due dates updated",
      );
    } else {
      await runBulkTaskUpdate(
        (task) => buildEditedTask(task.id, { due_date: null }),
        "Due dates cleared",
      );
    }
    closeBulkDueDateDialog();
  }, [
    bulkDueDateAction,
    bulkDueDate,
    runBulkTaskUpdate,
    buildEditedTask,
    closeBulkDueDateDialog,
  ]);

  const handleBulkTagChange = useCallback((newTag: NewTaskTag) => {
    setBulkTags((prev) => {
      const exists = prev.some(
        (tag) =>
          tag.tag === newTag.tag && tag.category_id === newTag.category_id,
      );
      if (exists) {
        return prev.filter(
          (tag) =>
            !(tag.tag === newTag.tag && tag.category_id === newTag.category_id),
        );
      }
      return [...prev, newTag];
    });
  }, []);

  const handleBulkCollaboratorToggle = useCallback((id: number) => {
    setBulkCollaborators((prev) =>
      prev.includes(id)
        ? prev.filter((collab) => collab !== id)
        : [...prev, id],
    );
  }, []);

  useEffect(() => {
    // Check if URLFilters has any values
    const hasValues = Object.values(URLFilters).some((value) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      } else if (typeof value === "object") {
        return (
          value &&
          typeof value === "object" &&
          Object.values(value).some((v) => v !== null && v !== "")
        );
      } else {
        return value !== null && value !== "";
      }
    });
    setHasFiltering(hasValues);
  }, [URLFilters]);

  async function fetchAllPrecache() {
    // if advanced filters are being used, fetch advanced data
    if (isUsingAdvancedFilters) {
      await fetchAdvancedData();
    }
    await getPrecacheTasks(false, true, true);
  }

  const selectedTaskCount = table.getSelectedRowModel().rows.length;
  const isBulkEditing =
    bulkActionSubmitting ||
    isBulkTagsDialogOpen ||
    isBulkCollaboratorsDialogOpen ||
    isBulkAssigneeDialogOpen ||
    isBulkDueDateDialogOpen ||
    isDeleteDialogOpen ||
    table.getIsSomeRowsSelected() ||
    table.getIsAllRowsSelected();

  return (
    <ResizablePanelGroup
      id={"panel-group-search-results"}
      direction="horizontal"
      className="flex flex-col h-full relative w-full overflow-hidden"
    >
      <ResizablePanel
        id={"panel-search-results"}
        order={1}
        className="h-full flex flex-col max-w-[100vw]"
      >
        <div className="w-full h-full p-2 pb-0 md:pb-2 flex flex-col overflow-hidden">
          {/* Search from url */}
          {(hasFiltering || !isNotSmall) && (
            <div className="flex flex-row items-center">
              <div className="px-2 font-bold">Search results</div>
              <div className="flex align-middle items-center cursor-pointer">
                {/* Star */}
                <FavoriteSearchDialog />
              </div>
              <Button
                variant="outline"
                className="ml-3 hidden md:flex"
                onClick={() => {
                  setChatWithSearchResults(true);
                }}
                disabled={!data.length}
              >
                <ChatBubbleIcon />
                Chat With Results
              </Button>
            </div>
          )}

          <div className="flex items-center pb-2 md:py-4 gap-3">
            <div className="flex flex-col md:flex-row justify-start md:justify-between w-full gap-2">
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Filter tasks..."
                  value={URLFilters.title || ""}
                  onChange={(event) => {
                    const value = event.target.value;
                    handleTitleSearch(value);
                  }}
                  className="w-auto max-w-sm"
                  ref={inputRef}
                  maxLength={255}
                />
                {/* Clear filters */}
                <Button
                  variant="outline"
                  onClick={() => {
                    clearFilters();
                  }}
                >
                  Clear Filters
                </Button>
              </div>
              {/* Fetch Precache */}
              <div className="ml-auto">
                {/* bulk select */}
                {(table.getIsSomeRowsSelected() ||
                  table.getIsAllRowsSelected()) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="mr-3">
                        Bulk Actions
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="gap-1 flex flex-col">
                      <DropdownMenuItem asChild>
                        <Button
                          variant="outline"
                          className="w-full cursor-pointer"
                          onClick={() => {
                            table.toggleAllRowsSelected(false);
                          }}
                          size={"sm"}
                        >
                          <Square />
                          Unselect All
                        </Button>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onSelect={() => setIsBulkTagsDialogOpen(true)}
                      >
                        <Tags />
                        Add or Remove Tag
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => setIsBulkCollaboratorsDialogOpen(true)}
                      >
                        <Users />
                        Add or Remove Collaborator
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => setIsBulkAssigneeDialogOpen(true)}
                      >
                        <User />
                        Assign To
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => setIsBulkDueDateDialogOpen(true)}
                      >
                        <Calendar />
                        Change Due Date
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => {
                          void runBulkStatusUpdate("complete");
                        }}
                      >
                        <CheckCircle />
                        Mark Complete
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => {
                          void runBulkStatusUpdate("incomplete");
                        }}
                      >
                        <Circle />
                        Mark Incomplete
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Button
                          variant="outline"
                          className="w-full cursor-pointer"
                          onClick={() => {
                            setIsDeleteDialogOpen(true);
                          }}
                          size={"sm"}
                        >
                          <Trash />
                          Bulk Delete
                        </Button>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                <TooltipProvider>
                  <Tooltip delayDuration={100}>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        className="mr-3"
                        onClick={() => {
                          fetchAllPrecache();
                        }}
                      >
                        Get Precache
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Clear precached data for search history and fetch from
                      server
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="ml-auto">
                      Columns <ChevronDownIcon className="ml-2 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="outline-gray-300 dark:outline-gray-700"
                  >
                    {table
                      .getAllColumns()
                      .filter((column) => column.getCanHide())
                      .map((column) => {
                        return (
                          <DropdownMenuCheckboxItem
                            key={column.id}
                            className="capitalize"
                            checked={column.getIsVisible()}
                            onCheckedChange={(value) =>
                              column.toggleVisibility(!!value)
                            }
                          >
                            {column.id}
                          </DropdownMenuCheckboxItem>
                        );
                      })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
          <div className="flex-grow overflow-auto overflow-x-hidden w-full rounded-md border relative">
            {/* Loading state for advanced search */}
            {isLoadingAdvanced && (
              <div className="absolute inset-0 bg-white/80 dark:bg-neutral-900/80 flex items-center justify-center z-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-sm text-muted-foreground">
                    Loading advanced search data...
                  </p>
                </div>
              </div>
            )}

            {/* Error state for advanced search */}
            {advancedError && (
              <div className="absolute inset-0 bg-white/90 dark:bg-neutral-900/90 flex items-center justify-center z-20">
                <div className="text-center max-w-md p-4">
                  <div className="text-red-500 mb-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-12 w-12 mx-auto"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-sm text-red-600 dark:text-red-400 mb-4">
                    {advancedError}
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      fetchAdvancedData();
                    }}
                  >
                    Retry
                  </Button>
                </div>
              </div>
            )}

            <Table className="w-full overflow-hidden">
              <TableHeader className="sticky top-0 z-10 bg-white dark:bg-background">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table
                    .getRowModel()
                    .rows.map((row) =>
                      isNotSmall ? (
                        <ViewSearchResultsRow
                          key={row.id}
                          row={row}
                          visibleColumns={row
                            .getVisibleCells()
                            .map((cell) => cell.column.id)}
                          inlineEditingDisabled={isBulkEditing}
                          bulkEditVersion={bulkEditVersion}
                        />
                      ) : (
                        <ViewSearchResultsRowMobile
                          key={row.id}
                          row={row}
                          visibleColumns={row
                            .getVisibleCells()
                            .map((cell) => cell.column.id)}
                          inlineEditingDisabled={isBulkEditing}
                        />
                      ),
                    )
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-end space-x-2 py-4">
            <div className="flex-1 text-sm text-muted-foreground">
              {table.getFilteredSelectedRowModel().rows.length} of{" "}
              {table.getFilteredRowModel().rows.length} row(s) selected.
            </div>
            <div className="space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </ResizablePanel>
      {chatWithSearchResults && (
        <>
          <ResizableHandle withHandle />
          <ResizablePanel
            id={"panel-search-results"}
            order={1}
            className="h-full min-w-[400px] max-w-[600px]"
          >
            <motion.div
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1 },
              }}
              transition={{ duration: 0.2 }}
              className="h-full"
            >
              <ChatMultipleSearchResults
                taskIDs={getFilteredTaskIDs()}
                closeChat={setChatWithSearchResults.bind(null, false)}
              />
            </motion.div>
          </ResizablePanel>
        </>
      )}

      {!chatWithSearchResults && viewingTaskID && (
        <SingleTaskViewSheet taskID={viewingTaskID} />
      )}
      <Dialog
        open={isBulkTagsDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeBulkTagsDialog();
            return;
          }
          setIsBulkTagsDialogOpen(true);
        }}
      >
        <DialogContent
          ref={setBulkCollaboratorsDialogContainer}
          className="sm:max-w-[520px]"
        >
          <DialogHeader>
            <DialogTitle>Bulk Tags</DialogTitle>
            <DialogDescription>
              Apply tags to {selectedTaskCount} selected tasks.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant={bulkTagAction === "add" ? "default" : "outline"}
                onClick={() => setBulkTagAction("add")}
                disabled={bulkActionSubmitting}
              >
                Add
              </Button>
              <Button
                type="button"
                variant={bulkTagAction === "remove" ? "default" : "outline"}
                onClick={() => setBulkTagAction("remove")}
                disabled={bulkActionSubmitting}
              >
                Remove
              </Button>
            </div>
            <CreateTaskTags
              id="bulk-tags"
              taskTags={bulkTags}
              handleFieldTags={handleBulkTagChange}
              createTagAllowed={false}
              modal={true}
            />
            <div className="text-xs text-muted-foreground">
              {bulkTagAction === "add"
                ? "Selected tags will be added to each task."
                : "Selected tags will be removed from each task."}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeBulkTagsDialog}
              disabled={bulkActionSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                void handleBulkTagsApply();
              }}
              disabled={bulkActionSubmitting}
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isBulkCollaboratorsDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeBulkCollaboratorsDialog();
            return;
          }
          setIsBulkCollaboratorsDialogOpen(true);
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Bulk Collaborators</DialogTitle>
            <DialogDescription>
              Update collaborators for {selectedTaskCount} selected tasks.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant={
                  bulkCollaboratorAction === "add" ? "default" : "outline"
                }
                onClick={() => setBulkCollaboratorAction("add")}
                disabled={bulkActionSubmitting}
              >
                Add
              </Button>
              <Button
                type="button"
                variant={
                  bulkCollaboratorAction === "remove" ? "default" : "outline"
                }
                onClick={() => setBulkCollaboratorAction("remove")}
                disabled={bulkActionSubmitting}
              >
                Remove
              </Button>
            </div>
            <CollaboratorPicker
              collaborators={bulkCollaboratorSelections}
              handleFieldCollaborators={handleBulkCollaboratorToggle}
              collaboratorRef={bulkCollaboratorRef}
              portalContainer={bulkCollaboratorsDialogContainer}
              side="bottom"
              align="start"
              containerClassName="w-full"
            />
            <div className="text-xs text-muted-foreground">
              {bulkCollaboratorAction === "add"
                ? "Selected collaborators will be added to each task."
                : "Selected collaborators will be removed from each task."}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeBulkCollaboratorsDialog}
              disabled={bulkActionSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                void handleBulkCollaboratorsApply();
              }}
              disabled={bulkActionSubmitting}
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isBulkAssigneeDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeBulkAssigneeDialog();
            return;
          }
          setIsBulkAssigneeDialogOpen(true);
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Bulk Assign</DialogTitle>
            <DialogDescription>
              Assign {selectedTaskCount} selected tasks to one assignee.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <AssigneePicker
              assignedTo={bulkAssignee}
              setAssignedTo={setBulkAssignee}
              handleFieldAssignedTo={(id) => setBulkAssignee(id)}
              modal={true}
              disabled={bulkActionSubmitting}
            />
            <div className="text-xs text-muted-foreground">
              Tasks will be assigned to the selected person.
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeBulkAssigneeDialog}
              disabled={bulkActionSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                void handleBulkAssigneeApply();
              }}
              disabled={bulkActionSubmitting}
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isBulkDueDateDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeBulkDueDateDialog();
            return;
          }
          setIsBulkDueDateDialogOpen(true);
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Bulk Due Date</DialogTitle>
            <DialogDescription>
              Set or clear due dates for {selectedTaskCount} selected tasks.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant={bulkDueDateAction === "set" ? "default" : "outline"}
                onClick={() => setBulkDueDateAction("set")}
                disabled={bulkActionSubmitting}
              >
                Set Date
              </Button>
              <Button
                type="button"
                variant={bulkDueDateAction === "clear" ? "default" : "outline"}
                onClick={() => setBulkDueDateAction("clear")}
                disabled={bulkActionSubmitting}
              >
                Clear Date
              </Button>
            </div>
            {bulkDueDateAction === "set" ? (
              <DueDatePicker
                dueDate={bulkDueDate}
                onDateChange={(date) => setBulkDueDate(date ?? null)}
                modal={true}
                disabled={bulkActionSubmitting}
              />
            ) : (
              <div className="text-sm text-muted-foreground">
                Due dates will be removed from each task.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeBulkDueDateDialog}
              disabled={bulkActionSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                void handleBulkDueDateApply();
              }}
              disabled={bulkActionSubmitting}
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DeleteTaskDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onDelete={async () => {
          await handleBulkDeleteTasks();
        }}
        type="bulk"
      />
    </ResizablePanelGroup>
  );
};

export default ViewSearchResults;
